'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, ShieldCheck, Loader2, Eye, EyeOff, User } from 'lucide-react';

export default function SuperAdminLoginPage() {
  const router = useRouter();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);

  // Check if already logged in
  useEffect(() => {
    const stored = localStorage.getItem('super_admin_auth');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.authed) {
          router.push('/super-admin/dashboard');
          return;
        }
      } catch {}
    }
    setChecking(false);
  }, [router]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!password.trim()) {
      setError('请输入密码');
      return;
    }
    setLoading(true);
    try {
      const body: Record<string, string> = { password: password.trim() };
      if (username.trim()) body.username = username.trim();

      const res = await fetch('/api/super-admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const admin = data.admin || { username: 'admin', name: '主管理员', role: 'owner' };
        localStorage.setItem('super_admin_auth', JSON.stringify({
          authed: true,
          loginAt: Date.now(),
          adminId: admin.id || '',
          username: admin.username,
          name: admin.name,
          role: admin.role || 'owner',
          token: data.token || '',
        }));
        router.push('/super-admin/dashboard');
      } else {
        setError(data.error || '登录失败');
      }
    } catch {
      setError('网络错误，请重试');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-gray-900">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-gray-900 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-yellow-500/5 rounded-full blur-3xl" />

      <Card className="w-full max-w-md mx-4 border-0 shadow-2xl bg-white/95 backdrop-blur-xl relative z-10">
        <CardContent className="pt-10 pb-10 px-8">
          {/* Logo */}
          <div className="flex flex-col items-center gap-5 mb-10">
            <div className="rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 p-4 shadow-xl shadow-amber-500/30">
              <ShieldCheck className="h-10 w-10 text-white" />
            </div>
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900">超级管理员</h1>
              <p className="text-sm text-gray-500 mt-1">SaaS 平台全局管理</p>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">用户名</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="管理员用户名（选填，兼容旧密码可留空）"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  className="pl-10 h-12 rounded-xl text-base"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">密码</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type={showPwd ? 'text' : 'password'}
                  placeholder="请输入密码"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-12 rounded-xl text-base"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-500 bg-red-50 rounded-xl px-4 py-3 border border-red-100">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-xl text-base font-semibold shadow-lg shadow-amber-500/25 transition-all duration-200"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  验证中...
                </span>
              ) : (
                '进入管理后台'
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              智能教务助理 SaaS 平台 &middot; 超级管理员入口
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
